import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';

/**
* AI:
* Status: 'in progress'
* Type: Service
* SubType: WidgetConfig
* Reason: Configured data provider for widget data fetching
*/

import {CnSimulationApiService} from '../../../service/api/cn-simulation-api/cn-simulation-api.service';
import {CnCompareRequestDto} from '../../../model/dto/api/cn-compare-request.dto';
import {CnCompareResultDto} from '../../../model/dto/api/cn-compare-result.dto';
import {CnContractFilterDto} from '../../../model/dto/api/cn-contract-filter.dto';
import {CnRunStatusDto} from '../../../model/dto/api/cn-run-status.dto';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetForm} from 'features/core/shared/widget/widget-form';
import {WidgetConfigFormLayout} from 'features/core/shared/widget/widget-config-form-layout';
import {WidgetConfigFormField} from 'features/core/shared/widget/widget-config-form-field';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';

/**
* Model-comparison orchestration component (FR-10 / FR-11), hosted by CnModelComparisonPage.
*
* Input panel: active model (required), scenario models (multiselect), contract filter,
* horizonYears (default 5), discountRatePercent (default 0), optional projectedQuota /
* assumedYoY per horizon year (A7), persistDecisionSnapshot toggle.
*
* 'Vergleich starten' calls compare() and polls the runId via CnSimulationApiService until
* SUCCESS / ERROR, showing a progress bar. On success it renders cn-model-comparison-result
* and, when a snapshot was persisted, cn-decision-snapshot.
*
* UI states (FR-16): idle / busy (progress) / ready / empty / error.
* NFR-CleanCore: NO calculation in the FE — all values come from the backend.
*/
@Component({
    selector: 'mvs-cn-model-comparison',
    templateUrl: './cn-model-comparison.component.html',
    styleUrls: ['./cn-model-comparison.component.scss'],
    standalone: false
})
export class CnModelComparisonComponent implements OnInit, OnChanges, OnDestroy {

    /** Model picker widgets (metadata-driven selectable lists). */
    activeModelWidget: WidgetData;
    scenarioModelWidget: WidgetData;

    // ----- Form state -----
    activeModelId: number | null = null;
    scenarioModelIds: number[] = [];
    contractFilter: CnContractFilterDto = {};
    horizonYears: number = 5;
    discountRatePercent: number = 0;
    persistDecisionSnapshot: boolean = false;

    /** Optional forecast arrays per horizon year (A7). */
    forecastRows: { year: number; projectedQuota: number | null; assumedYoY: number | null }[] = [];
    useForecast: boolean = false;

    // ----- Async / result state -----
    state: 'idle' | 'busy' | 'ready' | 'error' = 'idle';
    progress: number = 0;
    errorMessage: string | null = null;
    result: CnCompareResultDto | null = null;

    /** Model id -> label, populated from the picker selections for result headers. */
    modelLabels: { [modelId: number]: string } = {};

    initialized: boolean = false;

    constructor(private simulationApiService: CnSimulationApiService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
    * Initialize the comparison panel: seed the forecast rows and build the picker widgets.
    */
    initComponent(): void {
        this.rebuildForecastRows();
        this.refreshModelWidgets();
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['horizonYears']) {
            this.rebuildForecastRows();
        }
    }

    ngOnDestroy(): void {
    }

    /**
    * AI:
    * Status: 'in progress'
    * Type: Widget
    * SubType: TableWidget (createWidgetTableQl + WidgetForm custom layout)
    * Reason: Build the two selectable CommissionModel picker tables (Aktiv-Modell / Szenario-Modelle).
    *         Uses the curated createWidgetTableQl path with an explicit QL projection + WidgetForm so
    *         only business-meaningful columns (Name / Status / Gültig ab / Gültig bis) are shown — the
    *         picker is driven by the model NAME. The previous createWidgetTableEntityFkeyResolveQl path
    *         does NOT honor a WidgetForm layout, so it leaked the entity's default audit columns
    *         (Angelegt von, Sparte-ID, Freigegeben von, Farbe, Quelle, …). Row selection is unchanged.
    *         Reuses the exact builder shape of the reconciliation panel so both pages look identical.
    * @widget cn.comparison.active.model.widget / cn.comparison.scenario.model.widget (entity, list)
    */
    refreshModelWidgets(): void {
        this.activeModelWidget = WidgetFactory.createWidgetTableQl(
            'cn.comparison.active.model.widget',
            'Aktiv-Modell',
            CnModelComparisonComponent.buildModelPickerQlQuery('cn.comparison.active.model.widget'),
            WidgetDataParam.create('widgetForm', CnModelComparisonComponent.buildModelPickerWidgetForm())
        );
        this.scenarioModelWidget = WidgetFactory.createWidgetTableQl(
            'cn.comparison.scenario.model.widget',
            'Szenario-Modelle',
            CnModelComparisonComponent.buildModelPickerQlQuery('cn.comparison.scenario.model.widget'),
            WidgetDataParam.create('widgetForm', CnModelComparisonComponent.buildModelPickerWidgetForm())
        );
    }

    /**
    * Explicit QL projection for the CommissionModel picker tables. Only the columns that the curated
    * WidgetForm exposes are selected (root entity `e`); the persisted audit fields are never projected.
    */
    private static buildModelPickerQlQuery(alias: string): QlQueryDto {
        return {
            name: alias,
            type: 'view',
            start: {
                name: 'cn.CommissionModel',
                as: 'e',
                type: 'entity',
                fields: [
                    {name: 'id'},
                    {name: 'name'},      // Name (primary pick key)
                    {name: 'status'},    // Status
                    {name: 'validFrom'}, // Gültig ab
                    {name: 'validTo'},   // Gültig bis
                ],
            },
            fixedFilters: [],
            // e. prefix is mandatory for QL sorting field names on the root content reference.
            sortings: [new Sorting('e.name', true)],
        };
    }

    /**
    * Curated WidgetForm layout for the CommissionModel picker tables. Pins the column SET + ORDER and
    * applies explicit German uiLabels. Field ids are the QL result keys (`e.`-prefixed). hideFields=true
    * is the default-layout convention (fields with uiHidden=false are shown).
    */
    private static buildModelPickerWidgetForm(): WidgetForm {
        const columns: { field: string; uiLabel: string }[] = [
            {field: 'e.name', uiLabel: 'Name'},
            {field: 'e.status', uiLabel: 'Status'},
            {field: 'e.validFrom', uiLabel: 'Gültig ab'},
            {field: 'e.validTo', uiLabel: 'Gültig bis'},
        ];

        const layout = new WidgetConfigFormLayout();
        layout.hideFields = true;
        layout.fieldsConfigured = true;

        columns.forEach((column, index) => {
            const field = WidgetConfigFormField.create(column.field, '', '');
            field.uiLabel = column.uiLabel; // explicit German header
            field.uiPriority = index;       // preserves the curated column order
            layout.addField(field);
        });

        const widgetForm = new WidgetForm();
        widgetForm.setDefaultLayout(layout);
        return widgetForm;
    }

    /**
    * Rebuild the per-horizon-year forecast rows when horizonYears changes.
    */
    rebuildForecastRows(): void {
        const rows: { year: number; projectedQuota: number | null; assumedYoY: number | null }[] = [];
        for (let i = 1; i <= (this.horizonYears || 0); i++) {
            const existing = this.forecastRows.find(r => r.year === i);
            rows.push(existing ?? {year: i, projectedQuota: null, assumedYoY: null});
        }
        this.forecastRows = rows;
    }

    /**
    * Start the comparison run and poll until SUCCESS / ERROR.
    */
    startComparison(): void {
        if (!this.canStart()) {
            return;
        }

        this.state = 'busy';
        this.progress = 0;
        this.errorMessage = null;
        this.result = null;

        const request: CnCompareRequestDto = {
            activeModelId: this.activeModelId,
            scenarioModelIds: this.scenarioModelIds,
            contractFilter: this.contractFilter ?? {},
            horizonYears: this.horizonYears,
            discountRatePercent: this.discountRatePercent,
            persistDecisionSnapshot: this.persistDecisionSnapshot
        };

        if (this.useForecast) {
            request.projectedQuota = this.forecastRows.map(r => r.projectedQuota ?? 0);
            request.assumedYoY = this.forecastRows.map(r => r.assumedYoY ?? 0);
        }

        this.simulationApiService.compareAndPoll(request).subscribe({
            next: (value) => {
                if (this.isRunStatus(value)) {
                    this.progress = value.progress ?? 0;
                } else {
                    this.result = value as CnCompareResultDto;
                    this.state = 'ready';
                }
            },
            error: (err) => {
                this.state = 'error';
                this.errorMessage = err?.message || 'Der Modellvergleich ist fehlgeschlagen.';
            }
        });
    }

    /** Active model select handler (picker widget). */
    handleActiveModelSelect(event: any): void {
        const id = event?.objectId ?? event?.data?.['e.id'];
        const label = event?.data?.['e.name'];
        if (id) {
            this.activeModelId = id;
            if (label) {
                this.modelLabels[id] = label;
            }
        }
    }

    /** Scenario model multi-select handler (picker widget). */
    handleScenarioModelSelect(event: any): void {
        const id = event?.objectId ?? event?.data?.['e.id'];
        const label = event?.data?.['e.name'];
        if (!id) {
            return;
        }
        if (this.scenarioModelIds.includes(id)) {
            this.scenarioModelIds = this.scenarioModelIds.filter(x => x !== id);
        } else {
            this.scenarioModelIds = [...this.scenarioModelIds, id];
        }
        if (label) {
            this.modelLabels[id] = label;
        }
    }

    /** Whether the run can be started (active model + >=1 scenario). */
    canStart(): boolean {
        return !!this.activeModelId && this.scenarioModelIds.length > 0 && this.state !== 'busy';
    }

    /** Snapshot id from the result, if persisted. */
    get snapshotId(): number | null {
        return this.result?.snapshotId ?? null;
    }

    private isRunStatus(value: any): value is CnRunStatusDto {
        return value && typeof value.status === 'string' && 'progress' in value;
    }
}
